import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useState,
  useEffect,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Grid, Box, Card, Stack, Typography, Button } from "@mui/material";
import axios from "axios";
import { useCookies } from "react-cookie";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { PageContainer } from "../common";
import { AccountInput, Checkbox } from "../common/forms";

import { SAVED_USER_INFO } from "src/common/data/global";
import { getMemberDetail, signIn } from "src/services/accoutApi";
import { setTokenToClient } from "src/common/axiosClient";
import { setAuth, setUserDetail } from "src/store/authSlice";
import { useDispatch } from "src/store/Store";
import {
  ID_REQUIRED_MESSAGE,
  PASSWORD_REQUIRED_MESSAGE,
} from "src/common/constants";

import logo from "src/assets/images/logo2.png";
// import systemLogo from "src/assets/images/systemLogo.png";

// @ts-ignore
import background from "../../assets/background.mp4";

interface FormInputs {
  mbr_id: string;
  mbr_pw: string;
}

export const SignInSchema = yup
  .object({
    mbr_id: yup.string().required(ID_REQUIRED_MESSAGE),
    mbr_pw: yup.string().trim().required(PASSWORD_REQUIRED_MESSAGE),
  })
  .required();

const AuthLogin = ({
  title,
  subtitle,
  subtext,
}: {
  title?: ReactNode;
  subtitle: ReactNode;
  subtext?: ReactNode;
}) => {
  const dispatch = useDispatch();
  const [cookies, setCookie, removeCookie] = useCookies(["corp_mbr_id"]);

  const [rememberId, setRememberId] = useState(false);

  const methods = useForm<FormInputs>({
    defaultValues: {
      mbr_id: "",
      mbr_pw: "",
    },
    resolver: yupResolver(SignInSchema),
  });
  const { setValue, setError } = methods;

  useEffect(() => {
    /*저장된 쿠키값이 있으면, CheckBox TRUE 및 UserID에 값 셋팅*/
    if (cookies.corp_mbr_id !== undefined) {
      setValue("mbr_id", cookies.corp_mbr_id);
      setRememberId(true);
    }
  }, [setValue, cookies.corp_mbr_id]);

  const handleError = useCallback(
    (errorMessage: string) => {
      setError("mbr_id", { message: errorMessage }, { shouldFocus: true });
      setError("mbr_pw", {
        message: errorMessage,
      });
    },
    [setError]
  );
  const navigate = useNavigate();
  const onSubmit: SubmitHandler<FormInputs> = useCallback(
    async (data) => {
      const { mbr_id, mbr_pw } = data;

      try {
        // API 호출
        const response = await axios.post(
          "https://api.admin-hansol.com/admin/api/v1/auth/login",
          // "http://15.164.142.137:8080/admin/api/v1/auth/login",
          {
            username: mbr_id,
            password: mbr_pw,
          }
        );

        // 응답 데이터 처리
        if (response.status === 201) {
          const { accessToken } = response.data.content;
          console.log("aaaa", response.data.content);
          // 사용자 정보 객체 생성
          const userInfo = {
            id: mbr_id,
            accessToken: accessToken,
          };

          // sessionStorage에 저장
          sessionStorage.setItem("ssuser", JSON.stringify(userInfo));
          // 토큰을 클라이언트에 저장 (예: LocalStorage 또는 Cookies)
          setTokenToClient(accessToken);

          // 성공적으로 로그인하면 대시보드로 이동
          navigate("/");
        } else {
          handleError(
            "로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요."
          );
        }
      } catch (error: any) {
        // 에러 처리
        if (error.response && error.response.data) {
          handleError(error.response.data.message || "로그인 실패");
        } else {
          handleError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
        }
      }
    },
    [dispatch, handleError, navigate]
  );

  const handleRememberId = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setRememberId(e.target.checked);
  }, []);

  return (
    <>
      {title ? (
        <Typography fontWeight="700" variant="h3" mb={1}>
          {title}
        </Typography>
      ) : null}

      {subtext}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack>
            <AccountInput
              name="mbr_id"
              label="아이디"
              required
              disablePatternCheck
            />
            <AccountInput
              name="mbr_pw"
              label="비밀번호"
              type="password"
              required
              disablePatternCheck
            />

            <Stack gap="8px" direction="row" alignItems="center" my={2}>
              {/* <Typography
                component={Link}
                to="/find_account"
                fontWeight="500"
                sx={{
                  textDecoration: "none",
                  color: "primary.main"
                }}
              >
                계정 찾기
              </Typography>
              <Typography
                component={Link}
                to="/find_password"
                fontWeight="500"
                sx={{
                  textDecoration: "none",
                  color: "primary.main"
                }}
              >
                비밀번호 찾기
              </Typography> */}
              {/* <Typography>
                <Checkbox
                  checked={rememberId}
                  onChange={handleRememberId}
                  sx={{ padding: 0, paddingRight: "9px" }}
                />
                아이디 저장
              </Typography> */}
            </Stack>
          </Stack>
          <Box>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              fullWidth
            >
              로그인
            </Button>
          </Box>
        </form>
      </FormProvider>

      {subtitle}
    </>
  );
};

const SignIn = () => {
  return (
    <PageContainer>
      <video
        autoPlay
        muted
        loop
        id="video-background"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      >
        <source src={background} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <Box
        sx={{
          position: "relative",
          "&:before": {
            content: '""',
            // background: "radial-gradient(#d2f1df, #d3d7fa, #bad8f4)",
            background: "rgba(0, 0, 0, 0.5)",
            backgroundSize: "100% 100%",
            animation: "gradient 15s ease infinite",
            position: "absolute",
            height: "100%",
            width: "100%",
            opacity: "0.8",
          },
        }}
      >
        <Grid
          container
          spacing={0}
          justifyContent="center"
          sx={{ height: "100vh" }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            lg={5}
            xl={4}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Card
              elevation={9}
              sx={{ p: 4, zIndex: 1, width: "100%", maxWidth: "450px" }}
            >
              <AuthLogin
                title={
                  <>
                    <img
                      src={logo}
                      width="300px"
                      style={{ margin: "auto" }}
                      alt=""
                    />
                    {/* <img
                      src={systemLogo}
                      width="200px"
                      style={{ margin: "auto", marginTop: "20px" }}
                      alt=""
                    /> */}
                  </>
                }
                subtitle={
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    mt={3}
                  >
                    {/* <Typography
                      color="textSecondary"
                      variant="h6"
                      fontWeight="500"
                    >
                      계정이 없으신가요?
                    </Typography>
                    <Typography
                      component={Link}
                      to="/signup"
                      fontWeight="500"
                      sx={{
                        textDecoration: "none",
                        color: "primary.main"
                      }}
                    >
                      계정신청
                    </Typography> */}
                  </Stack>
                }
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </PageContainer>
  );
};

export default SignIn;
